import React from "react";
import * as Mui from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { designColors, StatCard } from "services/constants/designTokens";

// Same accent/tint mapping as LeaveStatusCards.js/PermissionStatusCards.js
// so "Pending"/"Approved"/"Rejected" read the same color everywhere in Team
// Zone. No "Cancelled" card here — the work_adjustment/actual-special-
// attendance data these On Duty rows come from never carries that status.
const STATUS_CARD_DEFS = [
  {
    key: "Pending",
    label: "Pending",
    icon: AccessTimeIcon,
    accent: "#F97316",
    tint: "#FFF7ED",
  },
  {
    key: "Approved",
    label: "Approved",
    icon: CheckCircleOutlineIcon,
    accent: "#16A34A",
    tint: "#EAFBF1",
  },
  {
    key: "Rejected",
    label: "Rejected",
    icon: HighlightOffIcon,
    accent: "#EF4444",
    tint: "#FEF2F2",
  },
  {
    key: "Cancelled",
    label: "Cancelled",
    icon: HighlightOffIcon,
    accent: "#64748B",
    tint: "#F1F5F9",
  },
];

const ALL_CARD_DEF = {
  key: "All",
  label: "All",
  icon: ListAltIcon,
  accent: "#1D4ED8",
  tint: "#EEF4FF",
};

// Bucket the API's `status` string into one of the three canonical statuses
// above (case/spelling tolerant), leaving anything unexpected untouched so
// an unrecognised status never silently disappears from the total.
export const canonicalOnDutyStatus = (status) => {
  const key = (status || "").trim().toLowerCase();
  if (key === "approved") return "Approved";
  if (key === "pending") return "Pending";
  if (key === "rejected") return "Rejected";
  if (key === "cancelled" || key === "canceled") return "Cancelled";
  return status;
};

// Single source of truth for a status's accent/tint pair, shared by these
// cards and OnDutyDetailPanel's header chip.
export const onDutyStatusVisual = (status) => {
  const canonical = canonicalOnDutyStatus(status);
  return (
    STATUS_CARD_DEFS.find((card) => card.key === canonical) || {
      key: canonical,
      accent: designColors.slate600,
      tint: designColors.slate100,
    }
  );
};

export const OnDutyStatusCards = ({ counts, activeFilter, onCardClick }) => {
  const totalCount = Object.values(counts || {}).reduce(
    (sum, count) => sum + (count || 0),
    0,
  );

  return (
    <Mui.Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        "@media (max-width:599.95px)": {
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          "& > :last-child:nth-child(odd)": { gridColumn: "1 / -1" },
        },
        gap: 1.5,
        mb: 2,
      }}
    >
      {[ALL_CARD_DEF, ...STATUS_CARD_DEFS].map((card) => {
        const isAll = card.key === "All";
        return (
          <StatCard
            activeBorder
            key={card.key}
            icon={card.icon}
            title={card.label}
            value={isAll ? totalCount : counts?.[card.key] || 0}
            accent={card.accent}
            tint={card.tint}
            isActive={isAll ? !activeFilter : activeFilter === card.key}
            onClick={onCardClick ? () => onCardClick(card.key) : undefined}
          />
        );
      })}
    </Mui.Box>
  );
};
